const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{
    display: inline-block;
    font-family: var(--ctl-font-sans, system-ui,-apple-system,Segoe UI,Roboto,Arial);
    vertical-align: middle;
  }

  .tag{
    display:inline-flex;
    align-items:center;
    gap: var(--ctl-space-2);
    white-space: nowrap;
    user-select: none;

    border: 1px solid transparent;
    border-radius: var(--ctl-radius-full);
    padding: var(--ctl-space-2) var(--ctl-space-2);

    font-size: var(--ctl-font-size-sm);
    font-weight: var(--ctl-font-weight-extrabold);
    letter-spacing: var(--ctl-tracking-normal);
    line-height: 1;
  }

  .icon{
    display:inline-flex;
    align-items:center;
    justify-content:center;
    line-height: 0;
    color: currentColor;
    opacity: var(--ctl-opacity-icon);
  }

  .label{
    display:inline-flex;
    align-items:center;
    min-width: 0;
  }

  /* tone => ink */
  :host([tone="neutral"]) { --ink: var(--ctl-color-muted); }
  :host([tone="info"])    { --ink: var(--ctl-color-info); }
  :host([tone="success"]) { --ink: var(--ctl-color-success); }
  :host([tone="warning"]) { --ink: var(--ctl-color-warning); }
  :host([tone="danger"])  { --ink: var(--ctl-color-danger); }
  :host(:not([tone]))     { --ink: var(--ctl-color-muted); }

  /* default style: soft pill */
  .tag{
    background: color-mix(in srgb, var(--ink) 12%, var(--ctl-color-surface));
    color: color-mix(in srgb, var(--ink) 86%, var(--ctl-color-text));
    border-color: color-mix(in srgb, var(--ink) 18%, var(--ctl-color-border));
  }

  /* remove button */
  .remove{
    all: unset;
    cursor: pointer;
    width: var(--ctl-size-tag-remove);
    height: var(--ctl-size-tag-remove);
    border-radius: var(--ctl-radius-full);
    display:inline-flex;
    align-items:center;
    justify-content:center;
    color: currentColor;
    opacity: var(--ctl-opacity-interactive);
    margin-right: -4px; /* visually tuck into pill edge */
  }
  .remove:hover{
    background: color-mix(in srgb, var(--ink) 14%, transparent);
    opacity: 1;
  }
  .remove:focus-visible{
    outline: none;
    box-shadow: var(--ctl-focus-ring);
  }

  .x{
    width: 12px;
    height: 12px;
    display:block;
  }

  /* disabled */
  :host([disabled]) .tag{
    opacity: var(--ctl-opacity-disabled);
    filter: saturate(.85);
  }
  :host([disabled]) .remove{
    cursor: not-allowed;
    opacity: var(--ctl-opacity-shine);
  }
  :host([disabled]) .remove:hover{
    background: transparent;
  }

  /* hide if no icon slotted */
  .icon:empty{ display:none; }
</style>

<span class="tag" part="tag">
  <span class="icon" part="icon"><slot name="icon"></slot></span>
  <span class="label" part="label"><slot></slot></span>
  <button id="remove" class="remove" part="remove" type="button" hidden>
    <svg class="x" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M18.3 5.7L12 12l6.3 6.3-1.4 1.4L10.6 13.4 4.3 19.7 2.9 18.3 9.2 12 2.9 5.7 4.3 4.3l6.3 6.3 6.3-6.3 1.4 1.4z"></path>
    </svg>
  </button>
</span>
`;

export class CtlTag extends HTMLElement {
  static get observedAttributes() {
    return ["tone", "removable", "disabled", "value", "name"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._removeBtn = this.shadowRoot.getElementById("remove");
    this._onRemove = this._onRemove.bind(this);
  }

  connectedCallback() {
    // defaults
    if (!this.hasAttribute("tone")) this.setAttribute("tone", "neutral");

    this._removeBtn.addEventListener("click", this._onRemove);
    this._sync();
  }

  disconnectedCallback() {
    this._removeBtn.removeEventListener("click", this._onRemove);
  }

  attributeChangedCallback() {
    this._sync();
  }

  // attrs
  get tone() { return this.getAttribute("tone") || "neutral"; }
  set tone(v) { this.setAttribute("tone", String(v || "neutral")); }

  get removable() { return this.hasAttribute("removable"); }
  set removable(v) { v ? this.setAttribute("removable", "") : this.removeAttribute("removable"); }

  get disabled() { return this.hasAttribute("disabled"); }
  set disabled(v) { v ? this.setAttribute("disabled", "") : this.removeAttribute("disabled"); }

  get value() { return this.getAttribute("value"); }
  set value(v) { v == null ? this.removeAttribute("value") : this.setAttribute("value", String(v)); }

  get name() { return this.getAttribute("name"); }
  set name(v) { v == null ? this.removeAttribute("name") : this.setAttribute("name", String(v)); }

  _sync() {
    // normalize tone
    const t = this.tone;
    if (!["neutral", "info", "success", "warning", "danger"].includes(t)) {
      this.setAttribute("tone", "neutral");
    }

    const showRemove = this.removable;
    this._removeBtn.hidden = !showRemove;

    // disabled behavior
    if (this.disabled) {
      this._removeBtn.setAttribute("disabled", "");
      this._removeBtn.tabIndex = -1;
      this.setAttribute("aria-disabled", "true");
    } else {
      this._removeBtn.removeAttribute("disabled");
      this._removeBtn.tabIndex = 0;
      this.removeAttribute("aria-disabled");
    }

    // accessible label for remove
    const labelText = (this.textContent || "").trim().replace(/\s+/g, " ");
    const name = this.getAttribute("name");
    const value = this.getAttribute("value");
    const suffix = labelText ? ` "${labelText}"` : "";
    const meta = name || value ? ` (${[name, value].filter(Boolean).join(":")})` : "";
    this._removeBtn.setAttribute("aria-label", `Remove tag${suffix}${meta}`);
  }

  _onRemove(e) {
    if (this.disabled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    const detail = {
      value: this.getAttribute("value"),
      name: this.getAttribute("name")
    };

    this.dispatchEvent(new CustomEvent("ctl-remove", {
      bubbles: true,
      composed: true,
      detail
    }));
  }
}

customElements.define("ctl-tag", CtlTag);
